import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AdminPanel = ({ settings, onUpdateSettings, error }) => {
  const [formData, setFormData] = useState({
    is_enabled: settings?.is_enabled || false,
    bypass_password: settings?.bypass_password || '',
    title: settings?.title || 'Coming Soon',
    description: settings?.description || 'We are working hard to bring you something amazing. Stay tuned!',
    background_image: settings?.background_image || ''
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    
    try {
      await onUpdateSettings(formData);
    } catch (error) {
      console.error('Error updating settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Coming Soon Settings</h1>
            <p className="text-muted-foreground mt-2">
              Control the site-wide coming soon page and access settings
            </p>
          </div>
          <a 
            href="/homepage"
            className="flex items-center space-x-2 px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>Back to Site</span>
          </a>
        </div>

        {/* Status Banner */}
        <div className={`p-4 rounded-lg border-l-4 mb-8 ${
          formData?.is_enabled 
            ? 'bg-amber-500/10 border-amber-500' :'bg-green-500/10 border-green-500'
        }`}>
          <div className="flex items-center space-x-3">
            <Icon 
              name={formData?.is_enabled ? "AlertTriangle" : "CheckCircle"} 
              size={20} 
              className={formData?.is_enabled ? "text-amber-500" : "text-green-500"}
            />
            <div>
              <p className={`font-medium ${
                formData?.is_enabled ? "text-amber-700 dark:text-amber-300" : "text-green-700 dark:text-green-300"
              }`}>
                {formData?.is_enabled ? 'Coming Soon Mode Active' : 'Site Accessible'}
              </p>
              <p className="text-sm text-muted-foreground">
                {formData?.is_enabled 
                  ? 'Visitors see the coming soon page unless they use the bypass password' :'All visitors can access the site normally'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg mb-6">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        {/* Settings Form */}
        <div className="bg-card rounded-lg border border-border p-6 space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Coming Soon Mode
              </label>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => handleInputChange('is_enabled', !formData?.is_enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData?.is_enabled ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData?.is_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm text-muted-foreground">
                  {formData?.is_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Page Title
              </label>
              <input
                type="text"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Coming Soon"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Description
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                rows={4}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                placeholder="Describe what visitors can expect..."
              />
            </div>

            {/* Bypass Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Bypass Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData?.bypass_password}
                  onChange={(e) => handleInputChange('bypass_password', e?.target?.value)}
                  className="w-full px-3 py-2 pr-10 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter bypass password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Users can enter this password to bypass the coming soon page
              </p>
            </div>

            {/* Background Image URL */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Background Image URL (Optional)
              </label>
              <input
                type="url"
                value={formData?.background_image}
                onChange={(e) => handleInputChange('background_image', e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="https://example.com/image.jpg"
              />
            </div>

            {/* Save Button */}
            <div className="flex items-center space-x-4 pt-4 border-t border-border">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground/20 border-t-primary-foreground rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={16} />
                    <span>Save Settings</span>
                  </>
                )}
              </button>
              
              <a
                href="/coming-soon"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="ExternalLink" size={16} />
                <span>Preview Page</span>
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;